// client/src/components/pages/LexiStar/Replay/trips/TripsDayLayers.tsx (moved from Geofencing/trips, ST-00318)
//
// The day view's map layers (ST-00159): every visible trip of the day as one GeoJSON line
// source (a casing under each line so it reads on Street and on Satellite), the numbered
// start badges (symbol layer; images drawn on demand by tripMapImages) and end rings
// (circle layer), and the day's stops as faint dots. One source per kind, never a React
// marker per point, so hundreds of trips stay smooth.
//
// Highlight: while a trip is highlighted (its row or its line pointed at, else the trip
// selected, ST-00318), every other trip dims and the highlighted one is drawn again, wider and fully opaque, on top of the
// lines (layer filters, so only the filter changes on hover).
import { memo, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/maplibre';
import type { ExpressionSpecification, FilterSpecification } from 'maplibre-gl';
import type { FleetStop } from '../../../../../types/clientPages/geofencing';
import {
  TRIP_CASING,
  type Basemap,
  type DayTrip,
  dayStopsFC,
  tripEndsFC,
  tripLinesFC,
  tripStartsFC,
} from './tripsDay';

/** Layers that answer the pointer in the day view (hover + click). */
export const DAY_TRIP_LAYER_IDS = ['day-trip-hit', 'day-trip-starts', 'day-trip-start-highlight'];
export const DAY_STOP_LAYER_ID = 'day-stops';
export const DAY_INTERACTIVE_LAYER_IDS = [...DAY_TRIP_LAYER_IDS, DAY_STOP_LAYER_ID];

const ROUND = { 'line-cap': 'round', 'line-join': 'round' } as const;
// Casing = line + 2 px (a 1 px rim each side), so the colour stays the line, not the rim.
const LINE_WIDTH: ExpressionSpecification = ['interpolate', ['linear'], ['zoom'], 8, 3, 14, 5];
const CASING_WIDTH: ExpressionSpecification = ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 7];
const HIGHLIGHT_WIDTH: ExpressionSpecification = ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 7];
const HIGHLIGHT_CASING_WIDTH: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  8,
  8,
  14,
  10,
];

/** `on` for the highlighted trip, `off` for the rest; `none` when nothing is highlighted. */
const byHighlight = (key: string | null, on: number, off: number, none: number) =>
  key == null
    ? none
    : (['case', ['==', ['get', 'key'], key], on, off] as unknown as ExpressionSpecification);

const onlyKey = (key: string | null): FilterSpecification => ['==', ['get', 'key'], key ?? ''];

export interface TripsDayLayersProps {
  /** The trips switched on, in list order. */
  trips: readonly DayTrip[];
  /** The stops of the vehicles switched on. */
  stops: readonly FleetStop[];
  basemap: Basemap;
  highlightKey: string | null;
  describeStop: (stop: FleetStop) => string;
}

function TripsDayLayers({
  trips,
  stops,
  basemap,
  highlightKey,
  describeStop,
}: TripsDayLayersProps) {
  const lines = useMemo(() => tripLinesFC(trips, basemap), [trips, basemap]);
  const starts = useMemo(() => tripStartsFC(trips, basemap), [trips, basemap]);
  const ends = useMemo(() => tripEndsFC(trips, basemap), [trips, basemap]);
  const stopsFC = useMemo(() => dayStopsFC(stops, describeStop), [stops, describeStop]);
  const casing = TRIP_CASING[basemap];

  return (
    <>
      {/* The day's stops, faint, under everything */}
      <Source id="day-stops-src" type="geojson" data={stopsFC}>
        <Layer
          id={DAY_STOP_LAYER_ID}
          type="circle"
          paint={{
            'circle-radius': 4,
            'circle-color': '#64748b',
            'circle-opacity': highlightKey == null ? 0.55 : 0.25,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-opacity': highlightKey == null ? 0.7 : 0.3,
          }}
        />
      </Source>

      <Source id="day-trips-src" type="geojson" data={lines}>
        <Layer
          id="day-trip-casing"
          type="line"
          layout={ROUND}
          paint={{
            'line-color': casing,
            'line-width': CASING_WIDTH,
            'line-opacity': byHighlight(highlightKey, 0, 0.12, 0.75),
          }}
        />
        <Layer
          id="day-trip-line"
          type="line"
          layout={ROUND}
          paint={{
            'line-color': ['get', 'color'],
            'line-width': LINE_WIDTH,
            'line-opacity': byHighlight(highlightKey, 0, 0.3, 0.92),
          }}
        />
        {/* Wide invisible line: an easy target for the pointer */}
        <Layer
          id="day-trip-hit"
          type="line"
          layout={ROUND}
          paint={{ 'line-color': '#000000', 'line-width': 16, 'line-opacity': 0 }}
        />
        <Layer
          id="day-trip-highlight-casing"
          type="line"
          layout={ROUND}
          filter={onlyKey(highlightKey)}
          paint={{
            'line-color': casing,
            'line-width': HIGHLIGHT_CASING_WIDTH,
            'line-opacity': 0.95,
          }}
        />
        <Layer
          id="day-trip-highlight"
          type="line"
          layout={ROUND}
          filter={onlyKey(highlightKey)}
          paint={{
            'line-color': ['get', 'color'],
            'line-width': HIGHLIGHT_WIDTH,
            'line-opacity': 1,
          }}
        />
      </Source>

      {/* End: a white ring in the trip's colour */}
      <Source id="day-trip-ends-src" type="geojson" data={ends}>
        <Layer
          id="day-trip-ends"
          type="circle"
          paint={{
            'circle-radius': 5,
            'circle-color': '#ffffff',
            'circle-stroke-width': 3,
            'circle-stroke-color': ['get', 'color'],
            'circle-opacity': byHighlight(highlightKey, 1, 0.35, 1),
            'circle-stroke-opacity': byHighlight(highlightKey, 1, 0.35, 1),
          }}
        />
      </Source>

      {/* Start: the trip's number on its colour. Lower numbers win a collision; the
          highlighted trip's badge is drawn again on top and never hidden. */}
      <Source id="day-trip-starts-src" type="geojson" data={starts}>
        <Layer
          id="day-trip-starts"
          type="symbol"
          layout={{
            'icon-image': ['get', 'icon'],
            'icon-allow-overlap': false,
            'icon-padding': 1,
            'symbol-sort-key': ['get', 'n'],
          }}
          paint={{ 'icon-opacity': byHighlight(highlightKey, 0, 0.35, 1) }}
        />
        <Layer
          id="day-trip-start-highlight"
          type="symbol"
          filter={onlyKey(highlightKey)}
          layout={{
            'icon-image': ['get', 'icon'],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-size': 1.15,
          }}
        />
      </Source>
    </>
  );
}

export default memo(TripsDayLayers);
